import Http from '@/services/http';
import translate, { formatNumber } from '@/services/translate';
import {
  CustomParameterRequest,
  ParametersTable,
  TableQueryRequest,
  ParameterAlarm,
  AllParametersNormHiddenStateQueryRequest,
  normsMap,
  diagnosticRuleEnumMap,
} from 'sigma-mappings';
import parametersFieldsDefinitions
  from '@/domain/data/installation/engineering/physicalChemical/parametersFieldsDefinitions';
import { getOperatorLabel } from '@/services/stringHelper';
import invert from 'lodash/invert';

const urls = {
  base: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/parametros`,
  hideVariable: (assetModuleId, parameterId) => `/engenharia/${assetModuleId}/fisico-quimico/parametros/${parameterId}/ocultar`,
  showVariable: (assetModuleId, parameterId) => `/engenharia/${assetModuleId}/fisico-quimico/parametros/${parameterId}/exibir`,
  getAllParametersNormHiddenState: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/parametros/todos-parametros-ocultos`,
  parameterById: (assetModuleId, id) => `/engenharia/${assetModuleId}/fisico-quimico/parametros/${id}`,
  getNormAlarm: (assetModuleId, norm) => `/alarme/fisico-quimico/${assetModuleId}/${norm}`,
  showAlarmNorm: (assetModuleId, norm) => `/alarme/fisico-quimico/${assetModuleId}/${norm}/exibir`,
  hideAlarmNorm: (assetModuleId, norm) => `/alarme/fisico-quimico/${assetModuleId}/${norm}/ocultar`,
};

function getNumberOptions(decimal = 0) {
  return {
    maximumFractionDigits: decimal,
    minimumFractionDigits: decimal,
  };
}

function getField(data, parameterIndex) {
  return parametersFieldsDefinitions[data.parameters[parameterIndex]] || {};
}

function getDiagnosticRuleTranslate(diagnosticRule) {
  return translate(`enums.engineering.diagnostic-rule.${diagnosticRule}`);
}
/* istanbul ignore next */
const error = {
  errorHandling: {
    responseMessageParams: {
      $MA029: (data) => {
        const field = getField(data, 2);
        return {
          NomeDoCampo: translate(field.name, { temperature: field.temperature }),
          FaixaInicial: formatNumber(field.minimum, getNumberOptions(field.precision)),
          FaixaFinal: formatNumber(field.maximum, getNumberOptions(field.precision)),
        };
      },
      $MA036: (data) => {
        const field = getField(data, 0);
        return {
          NomeDoCampo: translate(field.name, { temperature: field.temperature }),
        };
      },
      $MA037: (data) => {
        const field = getField(data, 0);
        return {
          0: translate(field.name, { temperature: field.temperature }),
          1: formatNumber(data.parameters[1], getNumberOptions(field.precision)),
          2: formatNumber(data.parameters[2], getNumberOptions(field.precision)),
        };
      },
      $MA039: (data) => {
        const field = parametersFieldsDefinitions[data.field] || {};
        return {
          field: translate(field.name, { temperature: field.temperature }),
        };
      },
      $MA058: (data) => {
        const field = parametersFieldsDefinitions[data.field] || {};
        return {
          NomeDoCampo: translate(field.name, { temperature: field.temperature }),
          NroCaracteres: field.maxLength,
        };
      },
      $ME066: (data) => {
        const { parameters } = data;
        const firstDiagnosticRule = diagnosticRuleEnumMap[parameters[0]];
        const secondaryDiagnosticRule = diagnosticRuleEnumMap[parameters[2]];
        const operator = getOperatorLabel(parameters[1]);
        const field = getField(data, 3);
        return {
          0: getDiagnosticRuleTranslate(firstDiagnosticRule),
          1: operator,
          2: getDiagnosticRuleTranslate(secondaryDiagnosticRule),
          3: translate(field.name, { temperature: field.temperature }),
        };
      },
      $MA067: data => data,
      $MA068: data => ({ name: (data.parameters || {}).nome }),
      $MA087: (data) => {
        const field = parametersFieldsDefinitions[data.field] || {};
        return {
          0: translate(field.name, { temperature: field.temperature }),
        };
      },
    },
  },
};

export default {
  getParametersTable(assetModuleId, payload) {
    const http = new Http();
    const mappedPayload = new TableQueryRequest(payload);
    return http
      .get(urls.base(assetModuleId), { params: mappedPayload })
      .then(({ data }) => new ParametersTable(data));
  },

  postParametersTable(assetModuleId, payload) {
    const http = new Http(error);
    const mappedPayload = new CustomParameterRequest(payload);
    return http
      .post(urls.base(assetModuleId), mappedPayload)
      .then(({ data }) => data);
  },

  putParameter(assetModuleId, payload) {
    const http = new Http(error);
    const mappedPayload = new CustomParameterRequest(payload);
    return http
      .put(urls.parameterById(assetModuleId, payload.id), mappedPayload)
      .then(({ data }) => data);
  },

  deleteParameter(assetModuleId, parameterId) {
    const http = new Http(error);
    return http
      .delete(urls.parameterById(assetModuleId, parameterId))
      .then(({ data }) => data);
  },

  toggleVariableVisibility(assetModuleId, parameterId, visibility) {
    const http = new Http();
    const url = visibility ? urls.hideVariable : urls.showVariable;
    return http
      .put(url(assetModuleId, parameterId))
      .then(({ data }) => data);
  },

  getNormAlarm(assetModuleId, norm) {
    const http = new Http();
    const mappedNorm = invert(normsMap)[norm];
    return http
      .get(urls.getNormAlarm(assetModuleId, mappedNorm))
      .then(({ data }) => new ParameterAlarm(data));
  },

  toggleNormAlarm(assetModuleId, enabled, norm) {
    const http = new Http();
    const mappedNorm = invert(normsMap)[norm];
    const url = enabled ? urls.hideAlarmNorm : urls.showAlarmNorm;
    return http
      .put(url(assetModuleId, mappedNorm))
      .then(({ data }) => data);
  },

  getAllParametersNormHiddenState(assetModuleId, payload) {
    const http = new Http();
    const mappedPayload = new AllParametersNormHiddenStateQueryRequest(payload);
    return http
      .get(urls.getAllParametersNormHiddenState(assetModuleId), { params: mappedPayload })
      .then(({ data }) => data);
  },
};
